/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { MOMENT_FORMATS, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Button } from '@coursera/coursera-ui';

import CodeBlockV2 from 'bundles/code-evaluator/components/CodeBlockV2';
import Modal from 'bundles/phoenix/components/Modal';
import RpcActionType from 'bundles/widget-simulator/components/tools/log/RpcActionType';
import type { ActionDetails, UserSuppliedObject } from 'bundles/widget-simulator/types';

import _t from 'i18n!nls/widget-simulator';

import 'css!./__styles__/RpcActionDetailsModal';

const styles = {
  alertContainer: css`
    .bt3-alert {
      padding: 15px;
      margin-bottom: 20px;
      border: 1px solid transparent;
      border-radius: 4px;
    }

    .bt3-alert h4 {
      margin-top: 0;
      color: inherit;
    }

    .bt3-alert > p,
    .bt3-alert > ul {
      margin-bottom: 0;
    }

    .bt3-alert > p + p {
      margin-top: 5px;
    }

    .bt3-alert-danger {
      background-color: var(--cds-color-feedback-error-invert-weak);
      border-color: var(--cds-color-red-100);
      color: var(--cds-color-red-800);
    }

    .bt3-alert-danger hr {
      border-top-color: var(--cds-color-red-100);
    }
  `,
};

// TODO: Move this to a utils file.
const objectToJson = (object: UserSuppliedObject): string | undefined => {
  try {
    return JSON.stringify(object, null, 2);
  } catch {
    return undefined;
  }
};

type ActionErrorsProps = {
  errors?: Array<string>;
};

// TODO: Move this to its own file.
const ActionErrors = ({ errors }: ActionErrorsProps) => {
  if (!errors || errors.length === 0) {
    return null;
  }

  return (
    <div css={styles.alertContainer}>
      <div className="rc-ActionErrors bt3-alert bt3-alert-danger">
        <h5 className="heading">Errors:</h5>
        <ul>
          {errors.map((error) => (
            <li key={error}>{error}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};

type ActionSectionProps = {
  type: 'request' | 'response';
  object: UserSuppliedObject;
  showHeader?: boolean;
};

// TODO: Move this to its own file.
const ActionSection = ({ type, object, showHeader = true }: ActionSectionProps) => {
  const json = objectToJson(object);

  const renderCodeEditor = () => {
    return <CodeBlockV2 readOnly={true} codeLanguage="json" expression={json} />;
  };

  return (
    <div className="action-section">
      {showHeader && (
        <h4>
          <RpcActionType type={type} />
        </h4>
      )}

      {json ? renderCodeEditor() : <div className="note">{_t('This action contains no #{type} data.', { type })}</div>}
    </div>
  );
};

type ActionDisplayProps = {
  action: ActionDetails;
  showHeader?: boolean;
};

// TODO: Move this to its own file.
const ActionDisplay = ({ action, showHeader = true }: ActionDisplayProps) => {
  if (action.type === 'request') {
    return <ActionSection type="request" object={action.request} showHeader={showHeader} />;
  } else {
    return <ActionSection type="response" object={action.response} showHeader={showHeader} />;
  }
};

// TODO: Move this to its own file.
const SupportingActionDisplay = ({ action, showHeader = true }: ActionDisplayProps) => {
  // If our action is a request, the supporting action is the response and vice versa.
  if (action.type === 'request') {
    return <ActionSection type="response" object={action.response} showHeader={showHeader} />;
  } else {
    return <ActionSection type="request" object={action.request} showHeader={showHeader} />;
  }
};

type PropsFromCaller = {
  action: ActionDetails;
  onClose: () => void;
};

type PropsToComponent = PropsFromCaller;

const RpcActionDetailsModal = (props: PropsToComponent) => {
  const { action, onClose } = props;
  const supportingActionType = action.type === 'request' ? 'response' : 'request';

  return (
    <Modal className="rc-RpcActionDetailsModal" modalName="RpcActionDetailsModal" handleClose={onClose}>
      <h3 className="modal-title">{action.name}</h3>

      <div className="modal-content">
        <div className="details">
          <ul>
            <li>
              <strong>Time:</strong> {formatDateTimeDisplay(action.time, MOMENT_FORMATS.MED_TIMEDATE_DISPLAY)}
            </li>
          </ul>
        </div>

        <ActionErrors errors={action.errors} />
        <ActionDisplay action={action} />

        <div className="supporting-action">
          <h6>
            Supporting <RpcActionType type={supportingActionType} />
          </h6>
          <SupportingActionDisplay action={action} showHeader={false} />
        </div>
      </div>

      <div className="modal-actions">
        <Button label={_t('Okay')} type="primary" size="sm" onClick={onClose} />
      </div>
    </Modal>
  );
};

export default RpcActionDetailsModal;
